import React, { useEffect, useRef, useState } from 'react';
import api from '../../config/axios';
import Header from '../common/Header';
import { useNavigate } from 'react-router-dom';
import './MaterialManagement.css';

interface Material {
  _id: string;
  name: string;
  unit: string;
  stock: number;
  costPrice: number;
  parLevel: number;
  alertThresholds: number[];
  stockPct: number | null;
  alertLevel: number | null;
  low: boolean;
}

interface Movement {
  _id: string;
  type: 'import' | 'consume' | 'restore' | 'adjust';
  quantity: number;
  stockAfter?: number;
  unitPrice?: number;
  totalCost?: number;
  note?: string;
  orderId?: string;
  createdAt: string;
}

const MOVEMENT_LABEL: Record<Movement['type'], string> = {
  import: 'Nhập kho',
  consume: 'Bán / tiêu hao',
  restore: 'Hoàn kho',
  adjust: 'Điều chỉnh',
};

const emptyForm = { name: '', unit: '', stock: 0, parLevel: 0, alertThresholds: '20,10' };

const MaterialManagement: React.FC<{ user: any }> = ({ user }) => {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [form, setForm] = useState<any>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [stockTarget, setStockTarget] = useState<Material | null>(null);
  const [stockForm, setStockForm] = useState({ quantity: '', unitPrice: '', note: '' });
  const [historyTarget, setHistoryTarget] = useState<Material | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/materials');
      setMaterials(res.data.data);
    } catch {
      setErr('Không tải được danh sách nguyên liệu');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const flash = (m: string, isErr = false) => {
    if (isErr) { setErr(m); setMsg(''); } else { setMsg(m); setErr(''); }
    setTimeout(() => { setMsg(''); setErr(''); }, 3000);
  };

  const openAdd = () => { setForm(emptyForm); setEditId(null); setShowForm(true); };
  const openEdit = (m: Material) => {
    setForm({ name: m.name, unit: m.unit, stock: m.stock, parLevel: m.parLevel, alertThresholds: (m.alertThresholds || []).join(',') });
    setEditId(m._id);
    setShowForm(true);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const thresholds = String(form.alertThresholds).split(',').map((s: string) => Number(s.trim())).filter((n: number) => !isNaN(n) && n > 0);
    const payload = { name: form.name, unit: form.unit, parLevel: Number(form.parLevel) || 0, alertThresholds: thresholds };
    try {
      if (editId) {
        await api.put(`/api/materials/${editId}`, payload);
        flash('Đã cập nhật nguyên liệu');
      } else {
        await api.post('/api/materials', { ...payload, stock: Number(form.stock) || 0 });
        flash('Đã thêm nguyên liệu');
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      flash(e.response?.data?.message || 'Lỗi lưu', true);
    }
  };

  const openStockIn = (m: Material) => {
    setStockTarget(m);
    setStockForm({ quantity: '', unitPrice: '', note: '' });
  };

  const openHistory = async (m: Material) => {
    setHistoryTarget(m);
    setMovements([]);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/api/materials/${m._id}/movements`);
      setMovements(res.data.data);
    } catch {
      flash('Không tải được lịch sử', true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const submitStockIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stockTarget) return;
    const quantity = Number(stockForm.quantity);
    if (!quantity || isNaN(quantity)) return flash('Số lượng không hợp lệ', true);
    const unitPrice = stockForm.unitPrice.trim() === '' ? undefined : Number(stockForm.unitPrice);
    try {
      await api.post(`/api/materials/${stockTarget._id}/stock`, {
        quantity,
        unitPrice,
        note: stockForm.note.trim() || 'Nhập thủ công',
      });
      setStockTarget(null);
      flash('Đã cập nhật kho');
      load();
    } catch (e: any) {
      flash(e.response?.data?.message || 'Lỗi nhập kho', true);
    }
  };

  const remove = async (m: Material) => {
    if (!window.confirm(`Xoá nguyên liệu "${m.name}"?`)) return;
    try {
      await api.delete(`/api/materials/${m._id}`);
      flash('Đã xoá');
      load();
    } catch {
      flash('Lỗi xoá', true);
    }
  };

  const importCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/materials/import', fd);
      flash(res.data.message || 'Đã import');
      load();
    } catch (e: any) {
      flash(e.response?.data?.message || 'Lỗi import CSV', true);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); window.location.href = '/'; };
  const rowClass = (m: Material) => (m.alertLevel !== null && m.alertLevel <= 10 ? 'mat-row danger' : m.low ? 'mat-row warn' : 'mat-row');

  return (
    <>
      <Header user={user} onLogout={onLogout}>
        <span className="user-role">{user.role === 'admin' ? 'Quản lý' : 'Nhân viên'}</span>
      </Header>
      <div className="material-container">
        <div className="material-header">
          <button className="material-back-btn" onClick={() => navigate('/')} title="Quay lại">←</button>
          <h2 className="material-title">QUẢN LÝ KHO NGUYÊN LIỆU</h2>
        </div>

        {msg && <div className="material-msg success">{msg}</div>}
        {err && <div className="material-msg error">{err}</div>}

        <div className="material-actions">
          <button className="material-btn primary" onClick={openAdd}>+ Thêm nguyên liệu</button>
          <button className="material-btn" onClick={() => fileRef.current?.click()}>⬆ Import CSV</button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={importCsv} />
          <span className="material-hint">CSV: cột name,unit,stock,parLevel,price</span>
        </div>

        {loading ? (
          <div className="material-loading">Đang tải...</div>
        ) : (
          <table className="material-table">
            <thead>
              <tr>
                <th>Nguyên liệu</th>
                <th>Tồn kho</th>
                <th>Giá vốn</th>
                <th>Mức đầy</th>
                <th>%</th>
                <th>Cảnh báo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {materials.length === 0 && (
                <tr><td colSpan={7} className="material-empty">Chưa có nguyên liệu nào.</td></tr>
              )}
              {materials.map((m) => (
                <tr key={m._id} className={rowClass(m)}>
                  <td>{m.name}</td>
                  <td>{m.stock} {m.unit}</td>
                  <td>{m.costPrice ? `${m.costPrice.toLocaleString()} đ` : '-'}</td>
                  <td>{m.parLevel || '-'}</td>
                  <td>{m.stockPct === null ? '-' : `${m.stockPct}%`}</td>
                  <td>
                    {m.alertLevel !== null
                      ? <span className={`mat-badge ${m.alertLevel <= 10 ? 'danger' : 'warn'}`}>dưới {m.alertLevel}%</span>
                      : <span className="mat-badge ok">OK</span>}
                  </td>
                  <td className="material-row-actions">
                    <button onClick={() => openStockIn(m)} title="Nhập kho">Nhập</button>
                    <button onClick={() => openHistory(m)} title="Lịch sử nhập/dùng">Lịch sử</button>
                    <button onClick={() => openEdit(m)} title="Sửa">Sửa</button>
                    <button className="danger" onClick={() => remove(m)} title="Xoá">Xoá</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {showForm && (
          <div className="material-modal-bg" onClick={() => setShowForm(false)}>
            <form className="material-modal" onClick={(e) => e.stopPropagation()} onSubmit={submitForm}>
              <h3>{editId ? 'Sửa nguyên liệu' : 'Thêm nguyên liệu'}</h3>
              <label>Tên</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <label>Đơn vị (g, ml, cái...)</label>
              <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} required />
              {!editId && (
                <>
                  <label>Tồn kho ban đầu</label>
                  <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                </>
              )}
              <label>Mức đầy (par level) — dùng tính % cảnh báo</label>
              <input type="number" value={form.parLevel} onChange={(e) => setForm({ ...form, parLevel: e.target.value })} />
              <label>Ngưỡng cảnh báo (% , cách nhau bởi dấu phẩy)</label>
              <input value={form.alertThresholds} onChange={(e) => setForm({ ...form, alertThresholds: e.target.value })} placeholder="20,10" />
              <div className="material-modal-actions">
                <button type="submit" className="material-btn primary">Lưu</button>
                <button type="button" className="material-btn" onClick={() => setShowForm(false)}>Huỷ</button>
              </div>
            </form>
          </div>
        )}

        {stockTarget && (
          <div className="material-modal-bg" onClick={() => setStockTarget(null)}>
            <form className="material-modal" onClick={(e) => e.stopPropagation()} onSubmit={submitStockIn}>
              <h3>Nhập kho: {stockTarget.name}</h3>
              <label>Số lượng ({stockTarget.unit}) — số âm để giảm</label>
              <input type="number" step="any" value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })} required />
              <label>Đơn giá nhập (đ / {stockTarget.unit})</label>
              <input type="number" step="any" min={0} value={stockForm.unitPrice} onChange={(e) => setStockForm({ ...stockForm, unitPrice: e.target.value })} placeholder="Giá mua cho 1 đơn vị" />
              <label>Ghi chú</label>
              <input value={stockForm.note} onChange={(e) => setStockForm({ ...stockForm, note: e.target.value })} placeholder="vd: Nhập hàng đầu tuần" />
              <div className="material-modal-actions">
                <button type="submit" className="material-btn primary">Xác nhận</button>
                <button type="button" className="material-btn" onClick={() => setStockTarget(null)}>Huỷ</button>
              </div>
            </form>
          </div>
        )}

        {historyTarget && (
          <div className="material-modal-bg" onClick={() => setHistoryTarget(null)}>
            <div className="material-modal material-history" onClick={(e) => e.stopPropagation()}>
              <h3>Lịch sử: {historyTarget.name}</h3>
              {historyLoading ? (
                <div className="material-loading">Đang tải...</div>
              ) : movements.length === 0 ? (
                <div className="material-empty">Chưa có biến động nào.</div>
              ) : (
                <div className="material-history-scroll">
                  <table className="material-table">
                    <thead>
                      <tr>
                        <th>Thời gian</th>
                        <th>Loại</th>
                        <th>Số lượng</th>
                        <th>Đơn giá</th>
                        <th>Thành tiền</th>
                        <th>Ghi chú</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movements.map((mv) => (
                        <tr key={mv._id}>
                          <td>{new Date(mv.createdAt).toLocaleString('vi-VN')}</td>
                          <td>
                            <span className={`mat-badge ${mv.quantity >= 0 ? 'ok' : 'warn'}`}>
                              {MOVEMENT_LABEL[mv.type]}
                            </span>
                          </td>
                          <td style={{ color: mv.quantity >= 0 ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                            {mv.quantity >= 0 ? '+' : ''}{mv.quantity} {historyTarget.unit}
                          </td>
                          <td>{mv.unitPrice ? `${mv.unitPrice.toLocaleString()} đ` : '-'}</td>
                          <td>{mv.totalCost ? `${mv.totalCost.toLocaleString()} đ` : '-'}</td>
                          <td>{mv.note || (mv.orderId ? `Đơn #${mv.orderId.slice(-6).toUpperCase()}` : '')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="material-modal-actions">
                <button type="button" className="material-btn" onClick={() => setHistoryTarget(null)}>Đóng</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MaterialManagement;
